import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { ButtonLink } from "@/components/ui/Button";
import { Link } from "@inertiajs/react";
import clsx from "clsx";
import StagesPanel, { type AdminStage } from "@/components/admin/StagesPanel";

type Requirement = {
  id: number;
  title: string;
  kind: string;
  required: boolean;
  active: boolean;
  position: number;
  due_offset_days: number | null;
  stage_name: string | null;
};

type Row = {
  id: number;
  name: string;
  location_label: string;
  starts_at_short_label: string | null;
  progress: { done: number; total: number; complete: boolean };
  completed_requirement_ids: number[];
};

export default function AdminRequirements({
  requirements,
  rows,
  stages,
}: {
  requirements: Requirement[];
  rows: Row[];
  stages: AdminStage[];
}) {
  const active = requirements.filter((r) => r.active);

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title="Requirements"
          subtitle="The steps every organizer works through for their own event, grouped into stages. Define them once here."
          actions={
            <ButtonLink href="/admin/requirements/new">
              New requirement
            </ButtonLink>
          }
        />

        <StagesPanel stages={stages} />

        {requirements.length === 0 ? (
          <EmptyState
            title="No requirements yet"
            description="Add the things every organizer must do - confirm the venue, submit a headcount, upload a receipt."
            action={
              <ButtonLink href="/admin/requirements/new">
                Add the first one
              </ButtonLink>
            }
          />
        ) : (
          <>
            <Panel title={`requirements.csv - ${requirements.length}`}>
              {requirements.map((req) => (
                <Link
                  key={req.id}
                  href={`/admin/requirements/${req.id}/edit`}
                  className="border-line/20 flex flex-wrap items-center gap-3 border-b py-2.5 last:border-b-0"
                >
                  <span className="text-ink-soft font-display text-caption w-6 shrink-0">
                    {req.position}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-body text-ink block truncate font-bold">
                      {req.title}
                    </span>
                    <span className="text-caption text-ink-soft">
                      {req.stage_name ?? "Everything else"}
                    </span>
                  </span>
                  <Badge tone="neutral">{req.kind}</Badge>
                  {req.due_offset_days != null && (
                    <Badge tone="info">
                      {req.due_offset_days > 0 ? "+" : ""}
                      {req.due_offset_days}d
                    </Badge>
                  )}
                  {!req.required && <Badge tone="neutral">Optional</Badge>}
                  {!req.active && <Badge tone="warning">Inactive</Badge>}
                </Link>
              ))}
            </Panel>

            {rows.length > 0 && active.length > 0 && (
              <section className="flex flex-col gap-4">
                <h2 className="font-display text-heading text-ink font-bold">
                  Across all events
                </h2>

                {/* Wide grid - scrolls inside itself rather than the page. */}
                <div className="border-line bg-surface shadow-card overflow-x-auto border-2">
                  <table className="w-full min-w-max border-collapse">
                    <thead>
                      <tr className="border-line bg-titlebar border-b-2">
                        <th className="font-display text-caption text-titlebar-ink bg-titlebar sticky left-0 z-10 px-3 py-2 text-left">
                          Event
                        </th>
                        {active.map((req) => (
                          <th
                            key={req.id}
                            className="font-display text-caption text-titlebar-ink px-3 py-2 text-left font-bold"
                            title={req.title}
                          >
                            <span className="block max-w-28 truncate">
                              {req.title}
                            </span>
                          </th>
                        ))}
                        <th className="font-display text-caption text-titlebar-ink px-3 py-2 text-left">
                          Done
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr
                          key={row.id}
                          className="border-line/20 border-b last:border-b-0"
                        >
                          <td className="bg-surface sticky left-0 z-10 px-3 py-2">
                            <span className="text-body text-ink font-bold">
                              {row.name}
                            </span>
                            <span className="text-caption text-ink-soft block">
                              {row.starts_at_short_label ?? "Date TBD"}
                            </span>
                          </td>

                          {active.map((req) => {
                            const done = row.completed_requirement_ids.includes(
                              req.id,
                            );
                            return (
                              <td key={req.id} className="px-3 py-2">
                                <span
                                  className={clsx(
                                    "flex h-5 w-5 items-center justify-center border-2 text-[11px] font-bold",
                                    done
                                      ? "border-line bg-leaf text-white"
                                      : "border-line/30 text-ink-soft",
                                  )}
                                >
                                  {done ? "✓" : ""}
                                </span>
                              </td>
                            );
                          })}

                          <td className="px-3 py-2">
                            {row.progress.complete ? (
                              <Badge tone="success">All set</Badge>
                            ) : (
                              <Badge tone="warning">
                                {row.progress.done}/{row.progress.total}
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
